import { ApolloError } from '@apollo/client';
import { createSelector } from 'reselect';

import { RootState, ThunkAction } from '../store';
import { getApolloClient } from '../shared/graphql/client';
import { USER_QUERY } from 'queries/user/user.query';
import { FetchUserQuery } from 'queries/user/user.query.generated';
import { User } from 'graphql.generated';

// Constants
export const GET_USER_ATTEMPT = 'user/GET_USER_ATTEMPT';
export const GET_USER_SUCCESS = 'user/GET_USER_SUCCESS';
export const GET_USER_FAILURE = 'user/GET_USER_FAILURE';
export const SET_LOGGED_IN = 'user/SET_LOGGED_IN';
export const SET_USER = 'user/SET_USER';

type Error = ApolloError;

export type LoggedInStatus =
    | 'initial'
    | 'loading'
    | 'isLoggedIn'
    | 'isNotLoggedIn'
    | 'error';

export type UserState = {
    loading: boolean;
    loggedInStatus: LoggedInStatus;
    error: Error[];
} & FetchUserQuery['user'];
interface GetUserAttemptAction {
    type: typeof GET_USER_ATTEMPT;
}
interface GetUserSuccessAction {
    type: typeof GET_USER_SUCCESS;
    user: FetchUserQuery['user'];
}
interface GetUserFailureAction {
    type: typeof GET_USER_FAILURE;
    error: Error;
}
interface SetLoggedInAction {
    type: typeof SET_LOGGED_IN;
    isLoggedIn: boolean;
}
interface SetUserAction {
    type: typeof SET_USER;
    user: User;
}

export type UserActionTypes =
    | GetUserAttemptAction
    | GetUserSuccessAction
    | GetUserFailureAction
    | SetLoggedInAction
    | SetUserAction;

// Actions
export const getUserAttempt = () => ({
    type: GET_USER_ATTEMPT
});

export const getUserSuccess = (user: FetchUserQuery['user']) => ({
    type: GET_USER_SUCCESS,
    user
});

export const getUserFailure = (error: Error) => ({
    type: GET_USER_FAILURE,
    error
});

export const setLoggedIn = (isLoggedIn: boolean) => ({
    type: SET_LOGGED_IN,
    isLoggedIn
});

export const setUser = (user: User) => ({
    type: SET_USER,
    user
});

// Reducer
export const initialState: UserState = {
    loading: false,
    loggedIn: false,
    loggedInStatus: 'initial',
    senderPropertyStatus: null,
    creationDate: null,
    userId: null,
    userUuid: null,
    error: []
};

const user = (
    state: UserState = initialState,
    action: UserActionTypes
): UserState => {
    switch (action.type) {
        case GET_USER_ATTEMPT:
            return {
                ...state,
                loading: true,
                loggedInStatus: 'loading'
            };
        case GET_USER_SUCCESS:
            return {
                ...state,
                loading: false,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                name: action.user?.name,
                email: action.user?.email,
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered
            };
        case GET_USER_FAILURE:
            return {
                ...state,
                loading: false,
                loggedInStatus: 'error',
                error: [action.error]
            };
        case SET_LOGGED_IN:
            return {
                ...state,
                loggedIn: action.isLoggedIn,
                loggedInStatus: action.isLoggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn'
            };
        case SET_USER:
            return {
                ...state,
                loggedIn: action.user?.loggedIn,
                loggedInStatus: action.user?.loggedIn
                    ? 'isLoggedIn'
                    : 'isNotLoggedIn',
                name: action.user?.name,
                email: action.user?.email,
                senderPropertyStatus: action.user?.senderPropertyStatus,
                creationDate: action.user?.creationDate,
                userId: action.user?.userId,
                userUuid: action.user?.userUuid,
                registered: action.user?.registered
            };
        default:
            return state;
    }
};

// Action creators
export const fetchUser = (): ThunkAction => async (dispatch) => {
    dispatch(getUserAttempt());

    try {
        const {
            data: { user }
        } = await getApolloClient().query<FetchUserQuery>({
            query: USER_QUERY
        });

        dispatch(getUserSuccess(user));
    } catch (error) {
        dispatch(getUserFailure(error));
    }
};

// Selectors
export const getUserSelector = (state: RootState): UserState => state.user;
export const isLoggedInSelector = createSelector<RootState, UserState, boolean>(
    getUserSelector,
    (user) => !!user.loggedIn
);
export const loggedInStatusSelector = createSelector<
    RootState,
    UserState,
    LoggedInStatus
>(getUserSelector, (user) => user.loggedInStatus);

export default user;
